// src/notifications.tsx
// Live counts of unread letters, unopened kudos, pending requests, and
// board posts since your last visit, shared by the layout's alert, the nav
// badges, and the dashboard.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Timestamp,
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { anonDocRef, ensureMyPenPalLinks, isPending, perAd } from "./penpal";
import { useBlocks } from "./blocks";

export type Counts = { letters: number; kudos: number; requests: number; board: number };

const empty: Counts = { letters: 0, kudos: 0, requests: 0, board: 0 };
const NotificationsContext = createContext<Counts>(empty);

export const useNotifications = () => useContext(NotificationsContext);

// Also remember what this browser has opened, so the alert still clears
// if the database refuses the "read" update.
const SEEN_KEY = "penpal-seen-ids";

function loadSeen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function rememberSeen(ids: string[]) {
  try {
    const seen = loadSeen();
    ids.forEach((id) => seen.add(id));
    localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen).slice(-500)));
    window.dispatchEvent(new Event(SEEN_KEY));
  } catch {
    // storage unavailable; the database flag still applies
  }
}

export function isUnreadLetter(id: string, data: { read?: boolean }) {
  return data.read !== true && !loadSeen().has(id);
}

export function isUnseenKudo(id: string, data: { seen?: boolean }) {
  return data.seen !== true && !loadSeen().has(id);
}

// New board posts are counted from your last visit to the board, which
// this browser remembers (a first visit starts counting from now).
const BOARD_SEEN = "penpal-board-seen";
const boardKey = (uid: string) => `${BOARD_SEEN}-${uid}`;

function boardSeenAt(uid: string) {
  try {
    const at = Number(localStorage.getItem(boardKey(uid)));
    if (at > 0) return at;
    localStorage.setItem(boardKey(uid), String(Date.now()));
  } catch {
    // storage unavailable; count from now
  }
  return Date.now();
}

export function markBoardSeen(uid: string) {
  try {
    localStorage.setItem(boardKey(uid), String(Date.now()));
  } catch {
    // storage unavailable
  }
  window.dispatchEvent(new Event(BOARD_SEEN));
}

// The team's welcome letter (src/welcome.ts) isn't in the database; its
// state lives on your profile and in this browser.
export const isWelcomeTrashed = () => loadSeen().has("welcome-trashed");

export async function markWelcome(uid: string, state: "read" | "trashed") {
  rememberSeen(state === "read" ? ["welcome"] : ["welcome", "welcome-trashed"]);
  try {
    await updateDoc(doc(db, "users", uid), { welcome: state });
  } catch (err) {
    console.warn("Couldn't save the welcome letter's state:", err);
  }
}

export async function markLetterRead(id: string) {
  rememberSeen([id]);
  try {
    await updateDoc(doc(db, "messages", id), { read: true });
  } catch (err) {
    console.warn("Couldn't mark letter as read:", err);
  }
}

export async function markKudosSeen(ids: string[]) {
  if (ids.length === 0) return;
  rememberSeen(ids);
  try {
    const batch = writeBatch(db);
    ids.forEach((id) => batch.update(doc(db, "kudos", id), { seen: true }));
    await batch.commit();
  } catch (err) {
    console.warn("Couldn't mark kudos as seen:", err);
  }
}

export function NotificationsProvider({
  uid,
  children,
}: {
  uid?: string;
  children: React.ReactNode;
}) {
  const [counts, setCounts] = useState<Omit<Counts, "board">>(empty);
  const [board, setBoard] = useState(0);
  // Blocked people never count toward notifications.
  const { ids: blocked } = useBlocks();
  const blockedRef = useRef(blocked);
  const recountRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    blockedRef.current = blocked;
    recountRef.current();
  }, [blocked]);

  // Older pen pals get their link (needed to exchange letters) restored.
  useEffect(() => {
    if (uid) ensureMyPenPalLinks(uid).catch((err) => console.warn(err));
  }, [uid]);

  useEffect(() => {
    if (!uid) return;

    // Latest snapshot from each listener, keyed "<kind>:<source>", so the
    // same doc seen by two listeners is only counted once.
    const sources = new Map<string, Map<string, any>>();

    const merged = (kind: string) => {
      const all = new Map<string, any>();
      sources.forEach((docs, key) => {
        if (key.startsWith(kind + ":")) docs.forEach((d, id) => all.set(id, d));
      });
      return Array.from(all);
    };

    const recount = () => {
      const ok = (who: string) => !blockedRef.current.has(who);
      setCounts({
        letters: merged("letters").filter(([id, d]) => ok(d.senderId) && !d.trashedByReceiver && isUnreadLetter(id, d))
          .length,
        kudos: merged("kudos").filter(([id, d]) => ok(d.from) && isUnseenKudo(id, d)).length,
        requests: merged("requests").filter(([, d]) => ok(d.from) && isPending(d.status))
          .length,
      });
    };
    recountRef.current = recount;

    const onError = (err: unknown) => console.warn("Notification listener:", err);
    const listen = (key: string, q: any) =>
      onSnapshot(
        q,
        (snap: any) => {
          const docs = new Map<string, any>();
          snap.docs.forEach((d: any) => docs.set(d.id, d.data()));
          sources.set(key, docs);
          recount();
        },
        onError
      );

    // The welcome letter counts as new mail until it's opened.
    const unsubWelcome = onSnapshot(
      doc(db, "users", uid),
      (snap) => {
        const state = snap.data()?.welcome;
        sources.set(
          "letters:welcome",
          new Map([
            ["welcome", { senderId: "", read: !!state, trashedByReceiver: state === "trashed" || isWelcomeTrashed() }],
          ])
        );
        recount();
      },
      onError
    );

    const unsubs = [
      unsubWelcome,
      listen("letters:to", query(collection(db, "messages"), where("receiverId", "==", uid))),
      listen("letters:legacy", query(collection(db, "messages"), where("receiverid", "==", uid))),
      listen("kudos:to", query(collection(db, "kudos"), where("to", "==", uid))),
      listen("requests:to", query(collection(db, "penpalRequests"), where("to", "==", uid))),
    ];

    // Mail addressed to the user's anonymous ads.
    let adUnsubs: (() => void)[] = [];
    const clearAdListeners = () => {
      adUnsubs.forEach((u) => u());
      adUnsubs = [];
      Array.from(sources.keys())
        .filter((k) => k.endsWith(":ad") || k.includes(":ad#"))
        .forEach((k) => sources.delete(k));
    };
    const unsubAnon = onSnapshot(
      anonDocRef(uid),
      (snap) => {
        clearAdListeners();
        const ids: string[] = snap.exists() ? snap.data().adIds || [] : [];
        perAd(ids, (adId) => {
          adUnsubs.push(
            listen(`letters:ad#${adId}`, query(collection(db, "messages"), where("receiverAdId", "==", adId))),
            listen(`kudos:ad#${adId}`, query(collection(db, "kudos"), where("toAd", "==", adId))),
            listen(`requests:ad#${adId}`, query(collection(db, "penpalRequests"), where("toAd", "==", adId)))
          );
          return null;
        });
        recount();
      },
      onError
    );

    window.addEventListener(SEEN_KEY, recount);
    return () => {
      unsubs.forEach((u) => u());
      unsubAnon();
      clearAdListeners();
      window.removeEventListener(SEEN_KEY, recount);
    };
  }, [uid]);

  // Board posts by other people since you last opened the board.
  useEffect(() => {
    if (!uid) return;
    let unsub = () => {};
    let latest: { uid?: string; at: number }[] = [];
    let since = 0;
    const recount = () =>
      setBoard(latest.filter((a) => a.at > since && a.uid !== uid && !(a.uid && blockedRef.current.has(a.uid))).length);
    const watch = () => {
      unsub();
      since = boardSeenAt(uid);
      latest = [];
      recount();
      unsub = onSnapshot(
        query(collection(db, "penpalAds"), where("timestamp", ">", Timestamp.fromMillis(since))),
        (snap) => {
          latest = snap.docs.map((d) => ({ uid: d.data().uid, at: d.data().timestamp?.toMillis?.() ?? 0 }));
          recount();
        },
        (err) => console.warn("Board listener:", err)
      );
    };
    watch();
    window.addEventListener(BOARD_SEEN, watch);
    return () => {
      unsub();
      window.removeEventListener(BOARD_SEEN, watch);
    };
  }, [uid, blocked]);

  const value = useMemo(() => ({ ...counts, board }), [counts, board]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
